/**
 * カーソル管理
 *
 * ダイアログやフリーインプットステップ内での
 * 複数発話（lines）を管理するためのカーソル状態を提供します。
 */

// ============================================
// Dialog カーソル
// ============================================

/**
 * ダイアログカーソル
 * 1つのダイアログステップ内で複数の発話を順に表示する際のインデックス
 */
export interface DialogCursor {
  stepId: string;
  index: number;
}

const DIALOG_CURSOR_FLAG_KEY = '__dialogCursor';

/**
 * ダイアログカーソルを読み取る
 */
export function readDialogCursor(flags: Record<string, unknown>): DialogCursor | null {
  const raw = (flags as Record<string, unknown>)[DIALOG_CURSOR_FLAG_KEY];
  if (!raw || typeof raw !== 'object') return null;

  const r = raw as Record<string, unknown>;
  if (typeof r.stepId !== 'string' || !Number.isFinite(r.index)) return null;

  return { stepId: r.stepId, index: Number(r.index) };
}

/**
 * ダイアログカーソルを書き込む
 */
export function writeDialogCursor(
  flags: Record<string, unknown>,
  cursor: DialogCursor
): Record<string, unknown> {
  return { ...(flags ?? {}), [DIALOG_CURSOR_FLAG_KEY]: cursor };
}

/**
 * ダイアログカーソルをクリアする
 */
export function clearDialogCursor(flags: Record<string, unknown>): Record<string, unknown> {
  if (!flags || typeof flags !== 'object') return {};
  const next = { ...flags } as Record<string, unknown>;
  delete next[DIALOG_CURSOR_FLAG_KEY];
  return next;
}

/**
 * ダイアログカーソルを進める
 * @returns 更新後のflags、またはnull（最後まで到達した場合）
 */
export function advanceDialogCursor(
  flags: Record<string, unknown>,
  stepId: string,
  totalLines: number
): { flags: Record<string, unknown>; isComplete: boolean } {
  const cursor = readDialogCursor(flags);
  const currentIndex = cursor && cursor.stepId === stepId ? cursor.index : 0;

  if (currentIndex < totalLines - 1) {
    // まだ次の発話がある
    return {
      flags: writeDialogCursor(flags, { stepId, index: currentIndex + 1 }),
      isComplete: false,
    };
  }

  // 最後まで到達 → カーソルをクリア
  return {
    flags: clearDialogCursor(flags),
    isComplete: true,
  };
}

/**
 * 現在のダイアログインデックスを取得
 */
export function getCurrentDialogIndex(
  flags: Record<string, unknown>,
  stepId: string
): number {
  const cursor = readDialogCursor(flags);
  if (cursor && cursor.stepId === stepId) {
    return cursor.index;
  }
  return 0;
}

// ============================================
// FreeInput 返答カーソル
// ============================================

/**
 * フリーインプット返答カーソル
 * AI返答の複数発話を順に表示する際のインデックス
 */
export interface FreeInputReplyCursor {
  stepId: string;
  index: number;
  lines: string[];
}

const FREE_INPUT_REPLY_CURSOR_FLAG_KEY = '__freeInputReplyCursor';

/**
 * フリーインプット返答カーソルを読み取る
 */
export function readFreeInputReplyCursor(
  flags: Record<string, unknown>
): FreeInputReplyCursor | null {
  const raw = (flags as Record<string, unknown>)[FREE_INPUT_REPLY_CURSOR_FLAG_KEY];
  if (!raw || typeof raw !== 'object') return null;

  const r = raw as Record<string, unknown>;
  if (
    typeof r.stepId !== 'string' ||
    !Number.isFinite(r.index) ||
    !Array.isArray(r.lines)
  ) {
    return null;
  }

  const lines = (r.lines as unknown[]).filter(
    (x: unknown) => typeof x === 'string'
  ) as string[];
  if (lines.length === 0) return null;

  return {
    stepId: r.stepId,
    index: Math.max(0, Math.min(lines.length - 1, Number(r.index))),
    lines,
  };
}

/**
 * フリーインプット返答カーソルを書き込む
 */
export function writeFreeInputReplyCursor(
  flags: Record<string, unknown>,
  cursor: FreeInputReplyCursor
): Record<string, unknown> {
  return { ...(flags ?? {}), [FREE_INPUT_REPLY_CURSOR_FLAG_KEY]: cursor };
}

/**
 * フリーインプット返答カーソルをクリアする
 */
export function clearFreeInputReplyCursor(
  flags: Record<string, unknown>
): Record<string, unknown> {
  if (!flags || typeof flags !== 'object') return {};
  const next = { ...flags } as Record<string, unknown>;
  delete next[FREE_INPUT_REPLY_CURSOR_FLAG_KEY];
  return next;
}

/**
 * フリーインプット返答カーソルを進める
 */
export function advanceFreeInputReplyCursor(
  flags: Record<string, unknown>,
  stepId: string
): { flags: Record<string, unknown>; isComplete: boolean } {
  const cursor = readFreeInputReplyCursor(flags);
  if (!cursor || cursor.stepId !== stepId) {
    return { flags, isComplete: true };
  }

  if (cursor.index < cursor.lines.length - 1) {
    return {
      flags: writeFreeInputReplyCursor(flags, { ...cursor, index: cursor.index + 1 }),
      isComplete: false,
    };
  }

  return {
    flags: clearFreeInputReplyCursor(flags),
    isComplete: true,
  };
}

/**
 * 現在のフリーインプット返答行を取得
 */
export function getCurrentFreeInputReplyLine(
  flags: Record<string, unknown>,
  stepId: string
): string | null {
  const cursor = readFreeInputReplyCursor(flags);
  if (!cursor || cursor.stepId !== stepId) {
    return null;
  }
  return cursor.lines[cursor.index] ?? null;
}

// ============================================
// フラグキー定数のエクスポート（テスト用）
// ============================================

export const CURSOR_FLAG_KEYS = {
  DIALOG: DIALOG_CURSOR_FLAG_KEY,
  FREE_INPUT_REPLY: FREE_INPUT_REPLY_CURSOR_FLAG_KEY,
} as const;
